/**
 * Landing header for `/business`: fixed over the hero (transparent), solid + blur after scroll.
 * Mega-menu on `/business/demo` remains `B2BMarketingNav`.
 */

import { useEffect, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { useTheme } from '@contexts/ThemeContext';
import { useAuth } from '@core/auth/useAuth';
import { b2cAppOrigin } from './B2BMarketingNav';

const NAV_LINKS = [
  { label: 'Home', to: '/business#hero' },
  { label: 'Problem', to: '/business#problem' },
  { label: 'Solution', to: '/business#solution' },
  { label: "Who it's for", to: '/business#who-its-for' },
  { label: 'Features', to: '/business#features' },
] as const;

const SCROLL_SOLID_PX = 16;

function scrollToSectionId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

interface B2BLandingHeaderProps {
  /** Always show the solid/scrolled style — use on pages with no hero (e.g. demo page). */
  forceSolid?: boolean;
  /** Hide the "Schedule demo" CTA — use on the demo page itself. */
  hideDemoButton?: boolean;
}

export function B2BLandingHeader({ forceSolid = false, hideDemoButton = false }: B2BLandingHeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(forceSolid);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { login } = useAuth();
  const jobSeekerHref = b2cAppOrigin();

  const onInPageNavClick = (e: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (location.pathname !== '/business') return;
    const hashIdx = to.indexOf('#');
    if (hashIdx === -1) return;
    const id = to.slice(hashIdx + 1);
    if (!id) return;
    e.preventDefault();
    setMobileOpen(false);
    if (location.hash === `#${id}`) {
      scrollToSectionId(id);
      return;
    }
    void navigate(to);
  };

  useEffect(() => {
    if (forceSolid) return;
    const onScroll = () => setScrolled(window.scrollY > SCROLL_SOLID_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [forceSolid]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      // Use removeProperty so we never accidentally leave an empty inline style
      if (prev) {
        document.body.style.overflow = prev;
      } else {
        document.body.style.removeProperty('overflow');
      }
    };
  }, [mobileOpen]);

  const onHero = !scrolled;
  /** Drawer is always light; lg+ uses contrast over purple hero until scroll. */
  const navLinkClass = [
    'block font-medium transition-colors max-lg:py-3 max-lg:text-slate-900 max-lg:hover:text-indigo-600 dark:max-lg:text-gray-100 dark:max-lg:hover:text-indigo-400',
    'lg:rounded-md lg:px-3 lg:py-2 lg:text-[15px]',
    onHero
      ? 'lg:text-white/95 lg:hover:text-white'
      : 'lg:text-slate-900 lg:hover:text-indigo-600 lg:dark:text-gray-100 lg:dark:hover:text-indigo-400',
  ].join(' ');

  /** Toolbar CTA beside theme toggle — `<button>`; glass on hero, gradient on solid bar. */
  const scheduleDemoBtnClass = [
    'cursor-pointer inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-all',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 sm:px-4 sm:text-[15px]',
    onHero
      ? 'border border-white/40 bg-white/15 text-white shadow-sm hover:bg-white/25'
      : 'border-0 bg-[#8E86FF] text-white shadow-md shadow-[rgba(142,134,255,0.35)] hover:bg-[#7B6EE8]',
  ].join(' ');

  const goDemo = () => {
    setMobileOpen(false);
    void navigate('/business/demo');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 flex min-h-[75px] py-4 tracking-wide transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        onHero
          ? 'bg-transparent shadow-none'
          : 'bg-white/95 shadow-[rgba(0,0,0,0.08)_0px_8px_24px_-8px] backdrop-blur-md dark:bg-gray-950/95 dark:shadow-[rgba(0,0,0,0.35)_0px_8px_24px_-8px]'
      }`}
    >
      <div className="flex w-full flex-wrap items-center gap-4 px-4 sm:px-10">
        <Link
          to="/business"
          className="flex shrink-0 items-center gap-2"
          onClick={() => setMobileOpen(false)}
        >
          <GGJLogoMark size={40} className="shrink-0" />
          <span
            className={`hidden max-w-[11rem] text-base font-bold leading-tight tracking-tight sm:inline md:max-w-none md:text-lg ${
              onHero ? 'text-white' : 'text-slate-900 dark:text-white'
            }`}
          >
            GoGrabJob Business
          </span>
        </Link>

        {mobileOpen ? (
          <button
            type="button"
            className="fixed inset-0 z-[55] bg-black/40 lg:hidden"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
        ) : null}

        <nav className="lg:ml-8 lg:flex lg:flex-1 lg:items-center" aria-label="Primary">
          {mobileOpen ? (
            <button
              type="button"
              className="fixed right-4 top-3 z-[100] flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white lg:hidden dark:border-gray-600 dark:bg-gray-900"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-4 w-4 text-slate-900 dark:text-gray-100" />
            </button>
          ) : null}

          <ul
            className={`max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:z-[60] max-lg:h-full max-lg:min-w-[300px] max-lg:w-2/3 max-lg:flex-col max-lg:gap-0 max-lg:overflow-y-auto max-lg:bg-white max-lg:p-4 max-lg:pt-14 max-lg:shadow-md dark:max-lg:bg-gray-950 lg:relative lg:flex lg:h-auto lg:w-auto lg:min-w-0 lg:flex-row lg:items-center lg:gap-1 lg:bg-transparent lg:p-0 lg:pt-0 lg:shadow-none xl:gap-x-1 ${
              mobileOpen ? 'max-lg:flex' : 'max-lg:hidden'
            }`}
          >
            <li className="max-lg:border-b max-lg:border-gray-200 max-lg:pb-4 lg:hidden dark:max-lg:border-gray-700">
              <Link to="/business" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <GGJLogoMark size={36} />
                <span className="font-bold text-slate-900 dark:text-white">GoGrabJob Business</span>
              </Link>
            </li>
            {NAV_LINKS.map((item) => (
              <li
                key={item.to}
                className="max-lg:border-b max-lg:border-gray-200 max-lg:px-3 dark:max-lg:border-gray-700"
              >
                <Link
                  to={item.to}
                  className={navLinkClass}
                  onClick={(e) => {
                    onInPageNavClick(e, item.to);
                    if (location.pathname !== '/business' || !item.to.includes('#')) setMobileOpen(false);
                  }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="max-lg:border-b max-lg:border-gray-200 max-lg:px-3 dark:max-lg:border-gray-700">
              <a
                href={jobSeekerHref}
                target="_blank"
                rel="noopener noreferrer"
                className={`${navLinkClass} lg:inline-flex`}
                onClick={() => setMobileOpen(false)}
              >
                Job seeker
              </a>
            </li>
            <li className="max-lg:px-3 max-lg:pt-3 lg:hidden">
              <button
                type="button"
                onClick={() => { setMobileOpen(false); login('/dashboard'); }}
                className="w-full cursor-pointer rounded-lg border border-indigo-300 px-4 py-2.5 text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950"
              >
                Already a member? Sign in
              </button>
            </li>
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className={`hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors sm:flex ${
              onHero
                ? 'text-white/90 hover:bg-white/10'
                : 'text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <button
            type="button"
            onClick={() => login('/dashboard')}
            className={[
              'hidden cursor-pointer whitespace-nowrap text-sm font-medium transition-colors sm:inline-flex items-center',
              onHero
                ? 'text-white/85 hover:text-white'
                : 'text-slate-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-400',
            ].join(' ')}
          >
            Already a member?&nbsp;<span className="font-semibold underline underline-offset-2">Sign&nbsp;in</span>
          </button>

          {!hideDemoButton && (
            <button type="button" className={scheduleDemoBtnClass} onClick={goDemo}>
              Schedule demo
            </button>
          )}

          <button
            type="button"
            className={`flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg lg:hidden ${
              onHero ? 'text-white' : 'text-slate-900 dark:text-white'
            }`}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </div>
    </header>
  );
}
